import React, { useEffect, useState } from 'react';
import { api } from '../../../lib/api';
import { Contact, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from './ui';

/* ---------------- Electronic business card (iter 145) ---------------- */

function cardEmailHtml(c) {
  return `<table cellpadding="0" cellspacing="0" style="width:340px;border:1px solid #d8d2c4;border-radius:10px;font-family:Arial,sans-serif;background:#ffffff;overflow:hidden">
<tr><td style="background:#003D80;padding:10px 16px;color:#fff">
<div style="font-size:15px;font-weight:bold">${c.staff_name}</div>
<div style="font-size:11px;opacity:.85">${[c.title, c.discipline].filter(Boolean).join(' · ')}</div>
</td></tr>
<tr><td style="padding:12px 16px;font-size:12px;color:#333;line-height:1.7">
<div style="font-weight:bold;color:#003D80">${c.clinic_name}</div>
${c.clinic_tagline ? `<div style="font-size:10px;color:#888">${c.clinic_tagline}</div>` : ''}
${c.staff_phone || c.clinic_phone ? `<div>☎ ${c.staff_phone || c.clinic_phone}</div>` : ''}
${c.staff_email ? `<div>✉ ${c.staff_email}</div>` : ''}
${c.clinic_address ? `<div>${c.clinic_address}</div>` : ''}
${c.clinic_website ? `<div><a href="${c.clinic_website}" style="color:#003D80">${c.clinic_website}</a></div>` : ''}
</td></tr></table>`;
}

function vCard(c) {
  const [first, ...rest] = (c.staff_name || '').split(' ');
  return ['BEGIN:VCARD', 'VERSION:3.0',
    `N:${rest.join(' ')};${first};;;`,
    `FN:${c.staff_name}`,
    `ORG:${c.clinic_name}`,
    `TITLE:${c.title}`,
    c.staff_phone ? `TEL;TYPE=WORK:${c.staff_phone}` : '',
    c.staff_email ? `EMAIL;TYPE=WORK:${c.staff_email}` : '',
    c.clinic_address ? `ADR;TYPE=WORK:;;${c.clinic_address};;;;` : '',
    c.clinic_website ? `URL:${c.clinic_website}` : '',
    'END:VCARD'].filter(Boolean).join('\r\n');
}

function CardModal({ emp, onClose }) {
  const [card, setCard] = useState(null);
  const [showHtml, setShowHtml] = useState(false);
  const [showVcf, setShowVcf] = useState(false);
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    api.get(`/hr/employees/${emp.employee_id}/business-card`).then(r => setCard(r.data)).catch(() => toast.error('Could not load card data.'));
  }, [emp.employee_id]);

  const copyHtml = async () => {
    const html = cardEmailHtml(card);
    try {
      // Rich copy — pasting into Gmail/Outlook renders the actual card.
      if (navigator.clipboard?.write && window.ClipboardItem) {
        await navigator.clipboard.write([new window.ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([html], { type: 'text/plain' }),
        })]);
        toast.success('Card copied — paste it straight into an email and the formatted card appears.');
        return;
      }
    } catch { /* ClipboardItem blocked — try plain text */ }
    try {
      await navigator.clipboard.writeText(html);
      toast.success('Email-ready HTML copied — paste it into your email signature or message.');
      return;
    } catch { /* clipboard API blocked (sandboxed iframe) — try execCommand */ }
    try {
      const ta = document.createElement('textarea');
      ta.value = html;
      ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) { toast.success('Email-ready HTML copied.'); return; }
    } catch { /* fall through */ }
    setShowHtml(true);
    toast.info('Your browser blocked automatic copying — select the HTML below and copy it manually (Ctrl/Cmd+C).');
  };

  const downloadVcf = () => {
    const vcf = vCard(card);
    const fname = `${(card.staff_name || 'contact').replace(/\s+/g, '_')}.vcf`;
    try {
      // data-URI download — survives sandboxed iframes better than blob URLs.
      const a = document.createElement('a');
      a.href = `data:text/vcard;charset=utf-8;base64,${btoa(unescape(encodeURIComponent(vcf)))}`;
      a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('.vcf downloading — if nothing happens, copy the raw card below or use "Email me the card".');
      setTimeout(() => setShowVcf(true), 400);
    } catch {
      setShowVcf(true);
      toast.info('Download blocked by the browser — copy the raw .vcf text below, or use "Email me the card".');
    }
  };

  const emailCard = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post(`/hr/employees/${emp.employee_id}/business-card/email`, {});
      if (data.sent) toast.success(`Card emailed to ${data.to} — HTML in the body + .vcf attached.`);
      else toast.warning('Email service not configured on this environment.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Email failed.');
    } finally { setEmailing(false); }
  };

  return (
    <Modal onClose={onClose}>
      <h3 className="font-heading font-bold text-lg mb-1 flex items-center gap-2"><Contact className="w-5 h-5 text-brand"/> Electronic business card</h3>
      <p className="text-xs text-fg-muted mb-4">Small enough to drop into any email. Copy the HTML for email signatures, or download the .vcf contact card.</p>
      {!card ? <div className="text-sm text-fg-muted py-6 text-center">Loading…</div> : (
        <>
          <div className="mx-auto w-[340px] rounded-xl border border-soft overflow-hidden shadow-md" data-testid="business-card-preview">
            <div className="bg-[#003D80] text-white px-4 py-2.5 flex items-center gap-3">
              {card.photo_url && <img src={card.photo_url} alt="" className="w-9 h-9 rounded-full object-cover border border-white/30"/>}
              <div>
                <div className="font-bold text-sm">{card.staff_name}</div>
                <div className="text-[10px] opacity-85">{[card.title, card.discipline].filter(Boolean).join(' · ')}</div>
              </div>
            </div>
            <div className="px-4 py-3 text-xs space-y-0.5 bg-white text-[#333]">
              <div className="font-bold text-[#003D80]">{card.clinic_name}</div>
              {card.clinic_tagline && <div className="text-[10px] text-fg-muted">{card.clinic_tagline}</div>}
              {(card.staff_phone || card.clinic_phone) && <div>☎ {card.staff_phone || card.clinic_phone}</div>}
              {card.staff_email && <div>✉ {card.staff_email}</div>}
              {card.clinic_address && <div>{card.clinic_address}</div>}
              {card.clinic_website && <div className="text-[#003D80]">{card.clinic_website}</div>}
            </div>
          </div>
          <div className="flex justify-center gap-2 mt-4 flex-wrap">
            <button className="btn-secondary text-xs" onClick={copyHtml} data-testid="card-copy-html-btn">Copy email HTML</button>
            <button className="btn-secondary text-xs" onClick={downloadVcf} data-testid="card-download-vcf-btn">Download .vcf</button>
            <button className="btn-primary text-xs inline-flex items-center gap-1.5" onClick={emailCard} disabled={emailing} data-testid="card-email-btn">
              {emailing && <Loader2 className="w-3 h-3 animate-spin"/>} Email me the card
            </button>
          </div>
          {showHtml && (
            <label className="block mt-4">
              <div className="text-xs text-fg-muted mb-1">Email HTML — select all and copy (Ctrl/Cmd+C)</div>
              <textarea className="input min-h-[110px] !text-[10px] font-mono" readOnly value={cardEmailHtml(card)}
                        onFocus={e => e.target.select()} data-testid="card-html-fallback"/>
            </label>
          )}
          {showVcf && (
            <label className="block mt-4">
              <div className="text-xs text-fg-muted mb-1">Raw .vcf — if the download didn't start, copy this into a file named <code>{(card.staff_name || 'contact').replace(/\s+/g, '_')}.vcf</code></div>
              <textarea className="input min-h-[110px] !text-[10px] font-mono" readOnly value={vCard(card)}
                        onFocus={e => e.target.select()} data-testid="card-vcf-fallback"/>
            </label>
          )}
        </>
      )}
    </Modal>
  );
}

export { CardModal, cardEmailHtml, vCard };
